import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { BookingScreen, ViewBookingAnalyticActions, ViewBookingController } from '../view_booking.controller';
import { CommitResult, BookingBasket, BookingBasketService } from '../booking_basket.service';
import { signal } from '@preact/signals-react';
import { TimeSlotApiService } from '@api_patient/timeslot_api.service';
import { UniversalConfig } from '@application/universal_config.service';
import { AsyncSignalValue } from '@component/async.component';
import type { ClientTimeslotApiModel, ClientPhysicalLocationApiModel } from '@api_patient/generated/clientv2/models';
import { AppointmentApiService } from '@api_patient/appointment_api.service';
import { GeneralApiService } from '@api_patient/general_api.service';
import { CellulaFormSubmitData } from '@cellula/react';
import { FileManagerService } from '@feature/file_manager/mod';
import { isHttpError } from '@application/http_error';

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenConclusionController implements ControllerProtocol {
	public data = signal<AsyncSignalValue<Readonly<PresentationModel>>>({
		flowState: 'pending',
		value: null,
	});
	public timeSlot = signal<AsyncSignalValue<Readonly<ClientTimeslotApiModel>>>({
		flowState: 'pending',
		value: null,
	});
	public readonly termsOfServiceUrl = signal<string | null>(null);
	public showUnavailableTimeSlotDialog = signal<boolean>(false);

	private ticket: string | null = null;
	private basket: BookingBasket | null = null;
	constructor(
		private readonly viewBookingController: ViewBookingController,
		private readonly analyticService: AnalyticService,
		private readonly bookingBasketService: BookingBasketService,
		private readonly universalConfig: UniversalConfig,
		private readonly timeSlotApiService: TimeSlotApiService,
		private readonly appointmentApiService: AppointmentApiService,
		private readonly generalApiService: GeneralApiService,
		private readonly fileManagerService: FileManagerService,
	) {
		this.basket = this.bookingBasketService.get();
	}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Booking: Conclusion',
		});

		if (!this.allowedToBeHere()) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Conclusion');

			this.viewBookingController.goToScreen(BookingScreen.Forms);

			return;
		}

		this.fetchTimeSlot();

		const policyDocumentsResult = await this.generalApiService.getPolicyDocuments(
			this.universalConfig.alias,
			this.universalConfig.selectedLanguageCode.peek(),
		);
		if (policyDocumentsResult.ok) {
			this.termsOfServiceUrl.value = policyDocumentsResult.ok.data.TermsOfServiceUrl;
		}
	}

	public async submit(formData: CellulaFormSubmitData): AsyncResult<CommitResult> {
		const flowSettings = this.viewBookingController.getFlowSettings();
		const reason = flowSettings.descriptionOfMatter ?? formData.reason ?? '';

		const fileTickets = this.fileManagerService.peakUploadedIdentities();

		const commitResult = await this.bookingBasketService.commit({
			reason,
			files: fileTickets,
			redRobinSessionId: flowSettings.redRobinSessionId ?? undefined,
		});
		if (commitResult.err) {
			if (isHttpError<{ ErrorCode: string }>(commitResult.err) && commitResult.err.data?.ErrorCode === 'SlotNotAvailable') {
				this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.SelectedTimeSlotNotIsAvailable);
				this.showUnavailableTimeSlotDialog.value = true;

				return Err(new SlotNotAvailableError('Selected time slot is not available'));
			}

			return Err(commitResult.err);
		}

		this.ticket = commitResult.ok.ticket;

		return Ok(commitResult.ok);
	}

	public async abortPayment() {
		if (!this.ticket) {
			return;
		}

		const cancelResult = await this.appointmentApiService.cancel(this.ticket);

		if (cancelResult.err) {
			this.viewBookingController.goToScreen(BookingScreen.Error);

			return;
		}

		this.bookingBasketService.set({
			timeSlotId: cancelResult.ok.data.NewTimeslot?.Id ?? null,
		});
		this.basket = this.bookingBasketService.get();

		await this.fetchTimeSlot();
	}

	private allowedToBeHere() {
		// If we don't have any user data, we can't be here.
		if (this.basket == null) {
			return false;
		}

		// If we don't have flow data, we can't be here.
		const flowData = this.viewBookingController.flowData.value.value;
		if (!flowData) {
			return false;
		}

		// If we have reason for visits, we need to have a selected reason for visit.
		if (
			flowData.useReasonForVisits &&
			this.viewBookingController.getFilteredReasonForVisits().length > 0 &&
			!this.viewBookingController.getSelectedReasonForVisit()
		) {
			return false;
		}

		// If we don't have a selected time slot, we can't be here.
		if (this.basket.timeSlotId == null) {
			return false;
		}

		return true;
	}

	private async fetchTimeSlot() {
		const timeSlotResult = await this.timeSlotApiService.getTimeSlot(
			this.universalConfig.selectedUnitId,
			this.basket?.timeSlotId ?? '',
		);
		if (timeSlotResult.err) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlot);
			this.viewBookingController.goToScreen(BookingScreen.Error);

			return;
		}

		let physicalLocation: ClientPhysicalLocationApiModel | null = null;

		if (timeSlotResult.ok.data.PhysicalLocationId) {
			physicalLocation =
				this.viewBookingController.flowData
					.peek()
					.value?.physicalLocations.find((location) => location.Id === timeSlotResult.ok.data.PhysicalLocationId) ?? null;
		}

		this.data.value = {
			value: {
				requirePayment: timeSlotResult.ok.data.RequiresPayment,
				timeSlot: timeSlotResult.ok.data,
				physicalLocation,
				reasonForVisit: this.viewBookingController.getSelectedReasonForVisit()?.Name ?? null,
				representativeNin: this.basket?.representativeNin ?? null,
			},
			flowState: 'accessible',
		};
	}
}

interface PresentationModel {
	requirePayment: boolean;
	timeSlot: ClientTimeslotApiModel;
	physicalLocation: ClientPhysicalLocationApiModel | null;
	reasonForVisit: string | null;
	representativeNin: string | null;
}

export class SlotNotAvailableError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'SlotNotAvailableError';
		Object.setPrototypeOf(this, SlotNotAvailableError.prototype);
	}
}
